import styled, { useTheme } from 'styled-components';
import { GardThemeType } from '@observatory/front-end/gard-theme';
import { ForwardedRef } from 'react';
import React from 'react';
import { LinkTo } from '../link/link';

/* eslint-disable-next-line */
export interface ButtonIconProps {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  href?: string;
  state?: unknown;
  disabled?: boolean;
  type: 'dashboard' | 'add' | 'edit' | 'delete';
  outlined?: boolean;
}

export const DashboardIcon = styled.svg<{ theme: GardThemeType }>`
  fill: none;
  width: 16px;
  height: 16px;
`;

const AddIcon = styled.svg<{ theme: GardThemeType }>`
  fill: none;
  width: 18px;
  height: 18px;
`;

const ButtonRef = React.forwardRef<
  HTMLButtonElement,
  React.PropsWithChildren<ButtonIconProps>
>((props, ref: ForwardedRef<HTMLButtonElement>) => (
  <button {...props} ref={ref} type="submit">
    {props.children}
  </button>
));

const StyledButton = styled(ButtonRef)<{ theme: GardThemeType }>`
  padding: ${(props) => props.theme.spacing.xs};
  height: 38px;
  width: 38px;
  border-radius: 38px;
  cursor: pointer;
  background-color: ${(props) => props.theme.colors.secondary};
  border: ${(props) => `1px solid ${props.theme.colors.secondary}`};
  display: flex;
  align-items: center;
  justify-content: center;
  :hover {
    background-color: ${(props) => props.theme.colors.secondaryVariant};
    border-color: ${(props) => props.theme.colors.secondaryVariant};
  }
  :disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PlainButton = styled(ButtonRef)<{ theme: GardThemeType }>`
  padding: ${(props) => props.theme.spacing.xs};
  height: 38px;
  width: 38px;
  cursor: pointer;
  background-color: none;
  background: none;
  text-decoration: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  :disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StyledLink = styled(LinkTo)<{ theme: GardThemeType }>`
  padding: ${(props) => props.theme.spacing.xs};
  height: 38px;
  width: 38px;
  border-radius: 38px;
  background-color: ${(props) => props.theme.colors.secondary};
  border: ${(props) => `1px solid ${props.theme.colors.secondary}`};
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  :hover {
    background-color: ${(props) => props.theme.colors.secondaryVariant};
    border-color: ${(props) => props.theme.colors.secondaryVariant};
  }
  :disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ButtonIcon = React.forwardRef(
  (
    props: React.PropsWithChildren<ButtonIconProps>,
    ref: ForwardedRef<HTMLButtonElement>
  ) => {
    const theme = useTheme() as GardThemeType;

    // Outlined buttons
    if ((props.onClick || !props.href) && !props.outlined) {
      return (
        <StyledButton
          ref={ref}
          onClick={props.onClick}
          disabled={props.disabled}
          type={props.type}
        >
          {props.type === 'dashboard' && (
            <DashboardIcon viewBox="0 0 14 14">
              <path
                d="M1 7.75H5.5C5.9125 7.75 6.25 7.4125 6.25 7V1C6.25 0.5875 5.9125 0.25 5.5 0.25H1C0.5875 0.25 0.25 0.5875 0.25 1V7C0.25 7.4125 0.5875 7.75 1 7.75ZM1 13.75H5.5C5.9125 13.75 6.25 13.4125 6.25 13V10C6.25 9.5875 5.9125 9.25 5.5 9.25H1C0.5875 9.25 0.25 9.5875 0.25 10V13C0.25 13.4125 0.5875 13.75 1 13.75ZM8.5 13.75H13C13.4125 13.75 13.75 13.4125 13.75 13V7C13.75 6.5875 13.4125 6.25 13 6.25H8.5C8.0875 6.25 7.75 6.5875 7.75 7V13C7.75 13.4125 8.0875 13.75 8.5 13.75ZM7.75 1V4C7.75 4.4125 8.0875 4.75 8.5 4.75H13C13.4125 4.75 13.75 4.4125 13.75 4V1C13.75 0.5875 13.4125 0.25 13 0.25H8.5C8.0875 0.25 7.75 0.5875 7.75 1Z"
                fill="white"
              />
            </DashboardIcon>
          )}
          {props.type === 'add' && (
            <AddIcon viewBox="0 0 16 16">
              <path
                d="M15.875 5.1875C15.875 5.85232 15.7441 6.51062 15.4896 7.12484C15.2352 7.73905 14.8623 8.29713 14.3922 8.76723C13.9221 9.23732 13.364 9.61023 12.7498 9.86464C12.1356 10.1191 11.4773 10.25 10.8125 10.25C10.1477 10.25 9.48938 10.1191 8.87516 9.86464C8.26095 9.61023 7.70287 9.23732 7.23277 8.76723C6.76268 8.29713 6.38977 7.73905 6.13536 7.12484C5.88095 6.51062 5.75 5.85232 5.75 5.1875C5.75 3.84484 6.28337 2.55717 7.23277 1.60777C8.18217 0.658369 9.46984 0.125 10.8125 0.125C12.1552 0.125 13.4428 0.658369 14.3922 1.60777C15.3416 2.55717 15.875 3.84484 15.875 5.1875ZM11.375 2.9375C11.375 2.78832 11.3157 2.64524 11.2102 2.53975C11.1048 2.43426 10.9617 2.375 10.8125 2.375C10.6633 2.375 10.5202 2.43426 10.4148 2.53975C10.3093 2.64524 10.25 2.78832 10.25 2.9375V4.625H8.5625C8.41332 4.625 8.27024 4.68426 8.16475 4.78975C8.05926 4.89524 8 5.03832 8 5.1875C8 5.33668 8.05926 5.47976 8.16475 5.58525C8.27024 5.69074 8.41332 5.75 8.5625 5.75H10.25V7.4375C10.25 7.58668 10.3093 7.72976 10.4148 7.83525C10.5202 7.94074 10.6633 8 10.8125 8C10.9617 8 11.1048 7.94074 11.2102 7.83525C11.3157 7.72976 11.375 7.58668 11.375 7.4375V5.75H13.0625C13.2117 5.75 13.3548 5.69074 13.4602 5.58525C13.5657 5.47976 13.625 5.33668 13.625 5.1875C13.625 5.03832 13.5657 4.89524 13.4602 4.78975C13.3548 4.68426 13.2117 4.625 13.0625 4.625H11.375V2.9375ZM10.8125 11.375C11.825 11.375 12.7812 11.132 13.625 10.7V13.625C13.625 14.2217 13.3879 14.794 12.966 15.216C12.544 15.6379 11.9717 15.875 11.375 15.875H2.375C1.77826 15.875 1.20597 15.6379 0.78401 15.216C0.362053 14.794 0.125 14.2217 0.125 13.625V8.37912L6.60838 11.87C6.69032 11.9141 6.78193 11.9372 6.875 11.9372C6.96807 11.9372 7.05968 11.9141 7.14162 11.87L8.729 11.015C9.38037 11.2479 10.0813 11.375 10.8125 11.375ZM2.375 4.625H4.64975C4.54568 5.75649 4.75542 6.89479 5.25594 7.91488C5.75646 8.93497 6.5284 9.79742 7.487 10.4075L6.875 10.7349L0.125 7.10113V6.875C0.125 6.27826 0.362053 5.70597 0.78401 5.28401C1.20597 4.86205 1.77826 4.625 2.375 4.625Z"
                fill="white"
              />
            </AddIcon>
          )}
          {props.type === 'edit' && (
            <AddIcon viewBox="0 0 18 18">
              <path
                d="M13.5038 16.148H.903783c-.238695 0-.467614.0948-.636396.2636-.168783.1688-.26360434.3977-.26360434.6364 0 .2387.09482134.4676.26360434.6364.168782.1688.397701.2636.636396.2636H13.5038c.2387 0 .4676-.0948.6364-.2636.1688-.1688.2636-.3977.2636-.6364 0-.2387-.0948-.4676-.2636-.6364-.1688-.1688-.3977-.2636-.6364-.2636Zm-12.600017-1.8h.081l3.752997-.342c.41112-.0409.79563-.2221 1.089-.513l8.10002-8.09998c.3144-.33213.4843-.77535.4725-1.23252-.0118-.45718-.2044-.89103-.5355-1.20648L11.3978.488017c-.3219-.302314-.7436-.4757774-1.185-.48739352C9.77137-.0109926 9.34108.140049 9.00378.425017L.903783 8.52502c-.290911.29337-.472046.67788-.513001 1.089L.00378266 13.367c-.01212394.1318.00498089.2647.05009504.3892.0451142.1244.1171273.2374.2109053.3308.084096.0834.18383.1494.293484.1942.109654.0448.22707.0675.345516.0668ZM10.1468 1.74802l2.457 2.457-1.8 1.755-2.41202-2.412 1.75502-1.8Zm-8.01002 8.019 5.067-5.031 2.43 2.43-5.04 5.03998-2.7.252.243-2.69098Z"
                fill="#fff"
              />
            </AddIcon>
          )}
          {props.type === 'delete' && (
            <AddIcon viewBox="0 0 18 18">
              <path
                d="M4.5 1.8c0-.47739.18964-.935227.52721-1.272792C5.36477.189642 5.82261 0 6.3 0h5.4c.4774 0 .9352.189642 1.2728.527208C13.3104.864773 13.5 1.32261 13.5 1.8v1.8h3.6c.2387 0 .4676.09482.6364.2636.1688.16879.2636.39771.2636.6364 0 .2387-.0948.46761-.2636.6364-.1688.16878-.3977.2636-.6364.2636h-.9621l-.7803 10.9278c-.0323.4541-.2355.8791-.5687 1.1894S14.0174 18 13.5621 18H4.437c-.45527 0-.89364-.1725-1.2268-.4828-.33317-.3103-.53638-.7353-.5687-1.1894L1.863 5.4H.9c-.238695 0-.467613-.09482-.636396-.2636C.0948211 4.96761 0 4.7387 0 4.5c0-.23869.0948211-.46761.263604-.6364C.432387 3.69482.661305 3.6.9 3.6h3.6V1.8Zm1.8 1.8h5.4V1.8H6.3v1.8ZM3.6666 5.4l.7713 10.8h9.1251l.7713-10.8H3.6666ZM7.2 7.2c.2387 0 .46761.09482.6364.2636.16878.16879.2636.39771.2636.6364v5.4c0 .2387-.09482.4676-.2636.6364-.16879.1688-.3977.2636-.6364.2636-.23869 0-.46761-.0948-.6364-.2636-.16878-.1688-.2636-.3977-.2636-.6364V8.1c0-.23869.09482-.46761.2636-.6364.16879-.16878.39771-.2636.6364-.2636Zm3.6 0c.2387 0 .4676.09482.6364.2636.1688.16879.2636.39771.2636.6364v5.4c0 .2387-.0948.4676-.2636.6364-.1688.1688-.3977.2636-.6364.2636-.2387 0-.4676-.0948-.6364-.2636-.16878-.1688-.2636-.3977-.2636-.6364V8.1c0-.23869.09482-.46761.2636-.6364.1688-.16878.3977-.2636.6364-.2636Z"
                fill="#fff"
              />
            </AddIcon>
          )}
        </StyledButton>
      );
    }

    if ((props.onClick || !props.href) && props.outlined) {
      return (
        <PlainButton
          ref={ref}
          onClick={props.onClick}
          disabled={props.disabled}
          type={props.type}
        >
          {props.type === 'dashboard' && (
            <DashboardIcon viewBox="0 0 14 14">
              <path
                d="M1 7.75H5.5C5.9125 7.75 6.25 7.4125 6.25 7V1C6.25 0.5875 5.9125 0.25 5.5 0.25H1C0.5875 0.25 0.25 0.5875 0.25 1V7C0.25 7.4125 0.5875 7.75 1 7.75ZM1 13.75H5.5C5.9125 13.75 6.25 13.4125 6.25 13V10C6.25 9.5875 5.9125 9.25 5.5 9.25H1C0.5875 9.25 0.25 9.5875 0.25 10V13C0.25 13.4125 0.5875 13.75 1 13.75ZM8.5 13.75H13C13.4125 13.75 13.75 13.4125 13.75 13V7C13.75 6.5875 13.4125 6.25 13 6.25H8.5C8.0875 6.25 7.75 6.5875 7.75 7V13C7.75 13.4125 8.0875 13.75 8.5 13.75ZM7.75 1V4C7.75 4.4125 8.0875 4.75 8.5 4.75H13C13.4125 4.75 13.75 4.4125 13.75 4V1C13.75 0.5875 13.4125 0.25 13 0.25H8.5C8.0875 0.25 7.75 0.5875 7.75 1Z"
                fill={theme.colors.primary}
              />
            </DashboardIcon>
          )}
          {props.type === 'add' && (
            <AddIcon viewBox="0 0 16 16">
              <path
                d="M15.875 5.1875C15.875 5.85232 15.7441 6.51062 15.4896 7.12484C15.2352 7.73905 14.8623 8.29713 14.3922 8.76723C13.9221 9.23732 13.364 9.61023 12.7498 9.86464C12.1356 10.1191 11.4773 10.25 10.8125 10.25C10.1477 10.25 9.48938 10.1191 8.87516 9.86464C8.26095 9.61023 7.70287 9.23732 7.23277 8.76723C6.76268 8.29713 6.38977 7.73905 6.13536 7.12484C5.88095 6.51062 5.75 5.85232 5.75 5.1875C5.75 3.84484 6.28337 2.55717 7.23277 1.60777C8.18217 0.658369 9.46984 0.125 10.8125 0.125C12.1552 0.125 13.4428 0.658369 14.3922 1.60777C15.3416 2.55717 15.875 3.84484 15.875 5.1875ZM11.375 2.9375C11.375 2.78832 11.3157 2.64524 11.2102 2.53975C11.1048 2.43426 10.9617 2.375 10.8125 2.375C10.6633 2.375 10.5202 2.43426 10.4148 2.53975C10.3093 2.64524 10.25 2.78832 10.25 2.9375V4.625H8.5625C8.41332 4.625 8.27024 4.68426 8.16475 4.78975C8.05926 4.89524 8 5.03832 8 5.1875C8 5.33668 8.05926 5.47976 8.16475 5.58525C8.27024 5.69074 8.41332 5.75 8.5625 5.75H10.25V7.4375C10.25 7.58668 10.3093 7.72976 10.4148 7.83525C10.5202 7.94074 10.6633 8 10.8125 8C10.9617 8 11.1048 7.94074 11.2102 7.83525C11.3157 7.72976 11.375 7.58668 11.375 7.4375V5.75H13.0625C13.2117 5.75 13.3548 5.69074 13.4602 5.58525C13.5657 5.47976 13.625 5.33668 13.625 5.1875C13.625 5.03832 13.5657 4.89524 13.4602 4.78975C13.3548 4.68426 13.2117 4.625 13.0625 4.625H11.375V2.9375ZM10.8125 11.375C11.825 11.375 12.7812 11.132 13.625 10.7V13.625C13.625 14.2217 13.3879 14.794 12.966 15.216C12.544 15.6379 11.9717 15.875 11.375 15.875H2.375C1.77826 15.875 1.20597 15.6379 0.78401 15.216C0.362053 14.794 0.125 14.2217 0.125 13.625V8.37912L6.60838 11.87C6.69032 11.9141 6.78193 11.9372 6.875 11.9372C6.96807 11.9372 7.05968 11.9141 7.14162 11.87L8.729 11.015C9.38037 11.2479 10.0813 11.375 10.8125 11.375ZM2.375 4.625H4.64975C4.54568 5.75649 4.75542 6.89479 5.25594 7.91488C5.75646 8.93497 6.5284 9.79742 7.487 10.4075L6.875 10.7349L0.125 7.10113V6.875C0.125 6.27826 0.362053 5.70597 0.78401 5.28401C1.20597 4.86205 1.77826 4.625 2.375 4.625Z"
                fill={theme.colors.primary}
              />
            </AddIcon>
          )}
          {props.type === 'edit' && (
            <AddIcon viewBox="0 0 18 18">
              <path
                d="M13.5038 16.148H.903783c-.238695 0-.467614.0948-.636396.2636-.168783.1688-.26360434.3977-.26360434.6364 0 .2387.09482134.4676.26360434.6364.168782.1688.397701.2636.636396.2636H13.5038c.2387 0 .4676-.0948.6364-.2636.1688-.1688.2636-.3977.2636-.6364 0-.2387-.0948-.4676-.2636-.6364-.1688-.1688-.3977-.2636-.6364-.2636Zm-12.600017-1.8h.081l3.752997-.342c.41112-.0409.79563-.2221 1.089-.513l8.10002-8.09998c.3144-.33213.4843-.77535.4725-1.23252-.0118-.45718-.2044-.89103-.5355-1.20648L11.3978.488017c-.3219-.302314-.7436-.4757774-1.185-.48739352C9.77137-.0109926 9.34108.140049 9.00378.425017L.903783 8.52502c-.290911.29337-.472046.67788-.513001 1.089L.00378266 13.367c-.01212394.1318.00498089.2647.05009504.3892.0451142.1244.1171273.2374.2109053.3308.084096.0834.18383.1494.293484.1942.109654.0448.22707.0675.345516.0668ZM10.1468 1.74802l2.457 2.457-1.8 1.755-2.41202-2.412 1.75502-1.8Zm-8.01002 8.019 5.067-5.031 2.43 2.43-5.04 5.03998-2.7.252.243-2.69098Z"
                fill={theme.colors.primary}
              />
            </AddIcon>
          )}
          {props.type === 'delete' && (
            <AddIcon viewBox="0 0 18 18">
              <path
                d="M4.5 1.8c0-.47739.18964-.935227.52721-1.272792C5.36477.189642 5.82261 0 6.3 0h5.4c.4774 0 .9352.189642 1.2728.527208C13.3104.864773 13.5 1.32261 13.5 1.8v1.8h3.6c.2387 0 .4676.09482.6364.2636.1688.16879.2636.39771.2636.6364 0 .2387-.0948.46761-.2636.6364-.1688.16878-.3977.2636-.6364.2636h-.9621l-.7803 10.9278c-.0323.4541-.2355.8791-.5687 1.1894S14.0174 18 13.5621 18H4.437c-.45527 0-.89364-.1725-1.2268-.4828-.33317-.3103-.53638-.7353-.5687-1.1894L1.863 5.4H.9c-.238695 0-.467613-.09482-.636396-.2636C.0948211 4.96761 0 4.7387 0 4.5c0-.23869.0948211-.46761.263604-.6364C.432387 3.69482.661305 3.6.9 3.6h3.6V1.8Zm1.8 1.8h5.4V1.8H6.3v1.8ZM3.6666 5.4l.7713 10.8h9.1251l.7713-10.8H3.6666ZM7.2 7.2c.2387 0 .46761.09482.6364.2636.16878.16879.2636.39771.2636.6364v5.4c0 .2387-.09482.4676-.2636.6364-.16879.1688-.3977.2636-.6364.2636-.23869 0-.46761-.0948-.6364-.2636-.16878-.1688-.2636-.3977-.2636-.6364V8.1c0-.23869.09482-.46761.2636-.6364.16879-.16878.39771-.2636.6364-.2636Zm3.6 0c.2387 0 .4676.09482.6364.2636.1688.16879.2636.39771.2636.6364v5.4c0 .2387-.0948.4676-.2636.6364-.1688.1688-.3977.2636-.6364.2636-.2387 0-.4676-.0948-.6364-.2636-.16878-.1688-.2636-.3977-.2636-.6364V8.1c0-.23869.09482-.46761.2636-.6364.1688-.16878.3977-.2636.6364-.2636Z"
                fill={theme.colors.primary}
              />
            </AddIcon>
          )}
        </PlainButton>
      );
    }

    if (!props.onClick && props.href) {
      return (
        <StyledLink href={props.href} state={props.state}>
          {props.type === 'dashboard' && (
            <DashboardIcon viewBox="0 0 14 14">
              <path
                d="M1 7.75H5.5C5.9125 7.75 6.25 7.4125 6.25 7V1C6.25 0.5875 5.9125 0.25 5.5 0.25H1C0.5875 0.25 0.25 0.5875 0.25 1V7C0.25 7.4125 0.5875 7.75 1 7.75ZM1 13.75H5.5C5.9125 13.75 6.25 13.4125 6.25 13V10C6.25 9.5875 5.9125 9.25 5.5 9.25H1C0.5875 9.25 0.25 9.5875 0.25 10V13C0.25 13.4125 0.5875 13.75 1 13.75ZM8.5 13.75H13C13.4125 13.75 13.75 13.4125 13.75 13V7C13.75 6.5875 13.4125 6.25 13 6.25H8.5C8.0875 6.25 7.75 6.5875 7.75 7V13C7.75 13.4125 8.0875 13.75 8.5 13.75ZM7.75 1V4C7.75 4.4125 8.0875 4.75 8.5 4.75H13C13.4125 4.75 13.75 4.4125 13.75 4V1C13.75 0.5875 13.4125 0.25 13 0.25H8.5C8.0875 0.25 7.75 0.5875 7.75 1Z"
                fill="white"
              />
            </DashboardIcon>
          )}
          {props.type === 'add' && (
            <AddIcon viewBox="0 0 16 16">
              <path
                d="M15.875 5.1875C15.875 5.85232 15.7441 6.51062 15.4896 7.12484C15.2352 7.73905 14.8623 8.29713 14.3922 8.76723C13.9221 9.23732 13.364 9.61023 12.7498 9.86464C12.1356 10.1191 11.4773 10.25 10.8125 10.25C10.1477 10.25 9.48938 10.1191 8.87516 9.86464C8.26095 9.61023 7.70287 9.23732 7.23277 8.76723C6.76268 8.29713 6.38977 7.73905 6.13536 7.12484C5.88095 6.51062 5.75 5.85232 5.75 5.1875C5.75 3.84484 6.28337 2.55717 7.23277 1.60777C8.18217 0.658369 9.46984 0.125 10.8125 0.125C12.1552 0.125 13.4428 0.658369 14.3922 1.60777C15.3416 2.55717 15.875 3.84484 15.875 5.1875ZM11.375 2.9375C11.375 2.78832 11.3157 2.64524 11.2102 2.53975C11.1048 2.43426 10.9617 2.375 10.8125 2.375C10.6633 2.375 10.5202 2.43426 10.4148 2.53975C10.3093 2.64524 10.25 2.78832 10.25 2.9375V4.625H8.5625C8.41332 4.625 8.27024 4.68426 8.16475 4.78975C8.05926 4.89524 8 5.03832 8 5.1875C8 5.33668 8.05926 5.47976 8.16475 5.58525C8.27024 5.69074 8.41332 5.75 8.5625 5.75H10.25V7.4375C10.25 7.58668 10.3093 7.72976 10.4148 7.83525C10.5202 7.94074 10.6633 8 10.8125 8C10.9617 8 11.1048 7.94074 11.2102 7.83525C11.3157 7.72976 11.375 7.58668 11.375 7.4375V5.75H13.0625C13.2117 5.75 13.3548 5.69074 13.4602 5.58525C13.5657 5.47976 13.625 5.33668 13.625 5.1875C13.625 5.03832 13.5657 4.89524 13.4602 4.78975C13.3548 4.68426 13.2117 4.625 13.0625 4.625H11.375V2.9375ZM10.8125 11.375C11.825 11.375 12.7812 11.132 13.625 10.7V13.625C13.625 14.2217 13.3879 14.794 12.966 15.216C12.544 15.6379 11.9717 15.875 11.375 15.875H2.375C1.77826 15.875 1.20597 15.6379 0.78401 15.216C0.362053 14.794 0.125 14.2217 0.125 13.625V8.37912L6.60838 11.87C6.69032 11.9141 6.78193 11.9372 6.875 11.9372C6.96807 11.9372 7.05968 11.9141 7.14162 11.87L8.729 11.015C9.38037 11.2479 10.0813 11.375 10.8125 11.375ZM2.375 4.625H4.64975C4.54568 5.75649 4.75542 6.89479 5.25594 7.91488C5.75646 8.93497 6.5284 9.79742 7.487 10.4075L6.875 10.7349L0.125 7.10113V6.875C0.125 6.27826 0.362053 5.70597 0.78401 5.28401C1.20597 4.86205 1.77826 4.625 2.375 4.625Z"
                fill="white"
              />
            </AddIcon>
          )}
        </StyledLink>
      );
    }

    return null;
  }
);

export default ButtonIcon;
